<template>
  <section
    ref="rootEl"
    class="cred-strip"
    :class="{ visible: isVisible }"
    aria-label="Social proof"
  >
    <div class="cred-inner">
      <!-- Stat 1 — Pitch competition -->
      <div class="cred-item cred-item-1">
        <div class="cred-icon cred-icon-amber">🏆</div>
        <div class="cred-text">
          <span class="cred-label">Top 10 from 250+ startups</span>
          <span class="cred-sub">Google x LvlUp Pitch Competition</span>
        </div>
      </div>

      <!-- Stat 2 — Reach -->
      <div class="cred-item cred-item-2">
        <div class="cred-icon cred-icon-blue">🌍</div>
        <div class="cred-text">
          <span class="cred-label">16,000+ trips planned</span>
          <span class="cred-sub">Trusted by users across 120 countries</span>
        </div>
      </div>

      <!-- Stat 3 — Substack (clickable) -->
      <a
        class="cred-item cred-item-3 cred-item-link"
        href="https://substack.com/@heytillyai"
        target="_blank"
        rel="noopener noreferrer"
        aria-label="Visit Tilly's Substack newsletter"
      >
        <div class="cred-icon cred-icon-coral">
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="currentColor"
            aria-hidden="true"
          >
            <path
              d="M22.539 8.242H1.46V5.406h21.08v2.836zM1.46 10.812V24L12 18.11 22.54 24V10.812H1.46zM22.54 0H1.46v2.836h21.08V0z"
            />
          </svg>
        </div>
        <div class="cred-text">
          <span class="cred-label">7,000+ subscribers</span>
          <span class="cred-sub">Travel newsletter on Substack</span>
        </div>
      </a>
    </div>
  </section>
</template>

<script>
import { ref, onMounted, onBeforeUnmount } from 'vue';

export default {
  name: 'CredibilityPanel',
  setup() {
    const rootEl = ref(null);
    const isVisible = ref(false);
    let observer = null;

    onMounted(() => {
      if (!('IntersectionObserver' in window) || !rootEl.value) {
        isVisible.value = true;
        return;
      }
      observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              isVisible.value = true;
              observer.unobserve(entry.target);
            }
          });
        },
        { threshold: 0.2 },
      );
      observer.observe(rootEl.value);
    });

    onBeforeUnmount(() => {
      if (observer) observer.disconnect();
    });

    return { rootEl, isVisible };
  },
};
</script>

<style scoped>
.cred-strip {
  width: 100%;
  background: #fdfaf6;
  padding: 2.75rem 1.5rem;
  background-image: url("data:image/svg+xml;utf8,<svg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='3' stitchTiles='stitch'/><feColorMatrix type='matrix' values='1 0 0 0 0  0 1 0 0 0  0 0 1 0 0  0 0 0 0.15 0'/></filter><rect width='100%' height='100%' filter='url(%23n)'/></svg>"),
    linear-gradient(180deg, #ffffff 0%, #fdfaf6 18%, #fdf8f3 100%);
  background-size: 220px 220px, 100% 100%;
}

.cred-inner {
  max-width: 980px;
  margin: 0 auto;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0;
  flex-wrap: wrap;
}

/* Each stat */
.cred-item {
  flex: 1;
  min-width: 220px;
  display: flex;
  align-items: center;
  gap: 0.95rem;
  padding: 0.5rem 1.5rem;
  opacity: 0;
  transform: translateY(14px);
  transition: opacity 0.65s ease,
    transform 0.65s cubic-bezier(0.22, 0.61, 0.36, 1);
}
.cred-item-1 {
  transition-delay: 0.05s;
}
.cred-item-2 {
  transition-delay: 0.18s;
}
.cred-item-3 {
  transition-delay: 0.31s;
}

.visible .cred-item {
  opacity: 1;
  transform: translateY(0);
}

/* Icon — soft tinted circle, the small upgrade that makes it feel intentional */
.cred-icon {
  width: 44px;
  height: 44px;
  flex-shrink: 0;
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.35rem;
  line-height: 1;
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.7),
    0 4px 12px -4px rgba(60, 40, 20, 0.15);
}
.cred-icon svg {
  display: block;
}

.cred-icon-amber {
  background: linear-gradient(135deg, #fff3d4 0%, #ffe5a8 100%);
  color: #b87a00;
}
.cred-icon-coral {
  background: linear-gradient(135deg, #ffe1d6 0%, #ffc9b6 100%);
  color: #c0533a;
}
.cred-icon-blue {
  background: linear-gradient(135deg, #d9ecf9 0%, #b9dcf2 100%);
  color: #2a7ab8;
}

/* Text stack */
.cred-text {
  display: flex;
  flex-direction: column;
  gap: 0.18rem;
  min-width: 0;
}

.cred-eyebrow {
  font-family: 'AirbnbCereal_W_Md', -apple-system, sans-serif;
  font-size: 0.66rem;
  font-weight: 600;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: #b88f5c;
  line-height: 1;
}

.cred-label {
  font-family: 'AirbnbCereal_W_Bd', -apple-system, sans-serif;
  font-size: 1.02rem;
  font-weight: 700;
  color: #1a1410;
  letter-spacing: -0.01em;
  line-height: 1.25;
}

.cred-sub {
  font-family: 'AirbnbCereal_W_Bk', -apple-system, sans-serif;
  font-size: 0.8rem;
  color: #8a7968;
  line-height: 1.4;
}

/* Soft hairline divider */
.cred-divider {
  width: 1px;
  height: 38px;
  background: linear-gradient(
    180deg,
    transparent 0%,
    rgba(26, 20, 16, 0.12) 50%,
    transparent 100%
  );
  flex-shrink: 0;
  opacity: 0;
  transition: opacity 0.6s ease 0.35s;
}
.visible .cred-divider {
  opacity: 1;
}

/* Tablet */
@media (max-width: 900px) {
  .cred-item {
    min-width: 180px;
    padding: 0.5rem 1rem;
    gap: 0.75rem;
  }
  .cred-label {
    font-size: 0.95rem;
  }
  .cred-sub {
    font-size: 0.76rem;
  }
}

/* Mobile: stack vertically */
@media (max-width: 640px) {
  .cred-strip {
    padding: 2rem 1.25rem;
  }
  .cred-inner {
    flex-direction: column;
    align-items: stretch;
    gap: 0;
    max-width: 380px;
  }
  .cred-item {
    padding: 0.85rem 0.5rem;
    min-width: 0;
  }
  .cred-divider {
    width: 100%;
    height: 1px;
    background: linear-gradient(
      90deg,
      transparent 0%,
      rgba(26, 20, 16, 0.1) 50%,
      transparent 100%
    );
  }
  .cred-icon {
    width: 40px;
    height: 40px;
    font-size: 1.2rem;
  }
}

/* Clickable Substack tile */
.cred-item-link {
  text-decoration: none;
  color: inherit;
  cursor: pointer;
  border-radius: 14px;
  /* Don't override transition shorthand — would reset stagger delay */
}
.cred-item-link:hover {
  transform: translateY(-3px);
  transition: transform 0.35s cubic-bezier(0.34, 1.56, 0.64, 1);
}
.cred-item-link:hover .cred-icon {
  transform: scale(1.06) rotate(-3deg);
  transition: transform 0.35s cubic-bezier(0.34, 1.56, 0.64, 1);
}
.cred-item-link:hover .cred-label {
  color: #ff6719;
  transition: color 0.2s ease;
}
.cred-item-link:focus-visible {
  outline: 2px solid #ff6719;
  outline-offset: 4px;
}
</style>
